import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Search, Users, UserCheck, UserX, ChevronDown, ExternalLink,
  CheckSquare, Square, ShieldCheck, ShieldOff,
} from 'lucide-react'
import { usersService } from '@/services/usersService'
import { useDebounce } from '@/hooks/useDebounce'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatDate, ROLE_LABELS } from '@/utils/formatters'

const ROLE_COLORS = {
  admin:    { bg: 'rgba(0,6,102,0.08)',     color: '#000666' },
  vendor:   { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  customer: { bg: 'rgba(118,118,131,0.08)', color: '#767683' },
}

export default function UsersPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()
  const navigate = useNavigate()

  const [search,     setSearch]     = useState('')
  const [role,       setRole]       = useState('')
  const [status,     setStatus]     = useState('')
  const [verified,   setVerified]   = useState('')
  const [selected,   setSelected]   = useState(new Set())
  const [bulkDialog, setBulkDialog] = useState({ open: false, action: null })
  const { page, limit, setPage } = usePagination(1, 20)
  const debouncedSearch = useDebounce(search, 400)
  const [dialog, setDialog] = useState({ open: false, user: null, action: null })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', { search: debouncedSearch, role, status, verified, page, limit }],
    queryFn: () => usersService.getAll({
      search:     debouncedSearch || undefined,
      role:       role     || undefined,
      isActive:   status === 'active' ? true : status === 'suspended' ? false : undefined,
      isVerified: verified === 'verified' ? true : verified === 'unverified' ? false : undefined,
      page,
      limit,
    }),
  })

  const users = data?.data || []
  const meta  = data?.meta || {}

  const toggleStatus = useMutation({
    mutationFn: ({ id, isActive }) => usersService.updateStatus(id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? t('users.activateSuccess') : t('users.suspendSuccess'))
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['users-summary'] })
      setDialog({ open: false, user: null, action: null })
    },
    onError: () => toast.error(t('users.genericError')),
  })

  const bulkMut = useMutation({
    mutationFn: ({ ids, isActive }) => usersService.bulkStatus(ids, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? t('users.bulkActivated', { count: selected.size }) : t('users.bulkSuspended', { count: selected.size }))
      qc.invalidateQueries({ queryKey: ['users'] })
      setSelected(new Set())
      setBulkDialog({ open: false, action: null })
    },
    onError: () => toast.error(t('users.bulkActionFailed')),
  })

  const openDialog = (user, action) => setDialog({ open: true, user, action })
  const handleConfirm = () => {
    if (!dialog.user) return
    toggleStatus.mutate({ id: dialog.user.id, isActive: dialog.action === 'activate' })
  }

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selected.size === users.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(users.map(u => u.id)))
    }
  }

  const handleSearchChange = (v) => { setSearch(v); setPage(1) }
  const handleRoleChange   = (v) => { setRole(v);   setPage(1) }
  const handleStatusChange = (v) => { setStatus(v); setPage(1) }

  return (
    <div className="space-y-5">
      {/* Filter bar */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-52">
          <Search size={16} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
          <input
            className="hg-input"
            style={{ paddingRight: 40 }}
            placeholder={t('users.search')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="relative">
          <select className="hg-input cursor-pointer min-w-32" style={{ paddingLeft:28 }} value={role} onChange={(e) => handleRoleChange(e.target.value)}>
            <option value="">{t('users.allRoles')}</option>
            <option value="customer">{t('users.roleCustomer')}</option>
            <option value="vendor">{t('users.roleVendor')}</option>
            <option value="admin">{t('users.roleAdmin')}</option>
          </select>
          <ChevronDown size={14} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
        </div>

        <div className="relative">
          <select className="hg-input cursor-pointer min-w-32" style={{ paddingLeft:28 }} value={status} onChange={(e) => handleStatusChange(e.target.value)}>
            <option value="">{t('users.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="suspended">{t('common.suspended')}</option>
          </select>
          <ChevronDown size={14} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
        </div>

        {/* Verified filter */}
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-36" style={{ paddingLeft:28 }} value={verified} onChange={(e) => { setVerified(e.target.value); setPage(1) }}>
            <option value="">{t('users.allVerification')}</option>
            <option value="verified">{t('users.verified')}</option>
            <option value="unverified">{t('users.unverified')}</option>
          </select>
          <ChevronDown size={14} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
        </div>

        {meta.total != null && (
          <span className="px-3 py-1.5 rounded-xl text-sm font-bold" style={{ background:'rgba(0,6,102,0.06)', color:'#000666', fontFamily:'Cairo, sans-serif' }}>
            {t('users.userCount', { count: meta.total?.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en') })}
          </span>
        )}

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:8, marginRight:'auto', padding:'6px 12px', borderRadius:12, background:'rgba(0,6,102,0.06)', border:'1px solid rgba(0,6,102,0.15)' }}>
            <span style={{ fontSize:12, fontWeight:700, color:'#000666', fontFamily:'Cairo, sans-serif' }}>{t('users.selectedCount', { count: selected.size })}</span>
            <button onClick={() => setBulkDialog({ open:true, action:'activate' })} style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:8, background:'rgba(27,109,36,0.1)', color:'#1B6D24', border:'none', cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:'Cairo, sans-serif' }}>
              <ShieldCheck size={12}/>{t('users.activateAll')}
            </button>
            <button onClick={() => setBulkDialog({ open:true, action:'suspend' })} style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:8, background:'rgba(186,26,26,0.1)', color:'#BA1A1A', border:'none', cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:'Cairo, sans-serif' }}>
              <ShieldOff size={12}/>{t('users.suspendAll')}
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="hg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <p className="font-bold" style={{ color:'#BA1A1A', fontFamily:'Cairo, sans-serif' }}>{t('common.loadFailed')}</p>
            <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">{t('common.retry')}</button>
          </div>
        ) : users.length === 0 ? (
          <EmptyState icon={Users} message={t('users.emptyMessage')} />
        ) : (
          <>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#F9F9F9', borderBottom:'2px solid #F3F3F3' }}>
                    <th style={{ padding:'12px 16px', width:40 }}>
                      <button onClick={toggleSelectAll} style={{ background:'none', border:'none', cursor:'pointer', color: selected.size === users.length && users.length > 0 ? '#000666' : '#C4C4C4', display:'flex', alignItems:'center' }}>
                        {selected.size === users.length && users.length > 0 ? <CheckSquare size={16}/> : <Square size={16}/>}
                      </button>
                    </th>
                    {[t('users.userHeader'), t('users.email'), t('users.phone'), t('users.role'), t('users.status'), t('users.verifiedHeader'), t('users.joinDate'), t('users.actions')].map((h) => (
                      <th key={h} style={{ textAlign:'right', padding:'12px 16px', fontSize:11, fontWeight:700, color:'#767683', whiteSpace:'nowrap', fontFamily:'Tajawal, sans-serif', letterSpacing:'0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      lang={lang}
                      last={idx === users.length - 1}
                      checked={selected.has(user.id)}
                      onCheck={() => toggleSelect(user.id)}
                      onActivate={() => openDialog(user, 'activate')}
                      onSuspend={()   => openDialog(user, 'suspend')}
                      onView={()      => navigate(`/users/${user.id}`)}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding:'0 16px 16px' }}>
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={dialog.open}
        title={dialog.action === 'activate' ? t('users.activateAccountTitle') : t('users.suspendAccountTitle')}
        message={dialog.action === 'activate'
          ? t('users.confirmActivateBody', { name: dialog.user?.name })
          : t('users.confirmSuspendBody', { name: dialog.user?.name })}
        variant={dialog.action === 'suspend' ? 'danger' : 'warning'}
        loading={toggleStatus.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setDialog({ open: false, user: null, action: null })}
      />

      <ConfirmDialog
        open={bulkDialog.open}
        title={bulkDialog.action === 'activate' ? t('users.bulkActivateTitle', { count: selected.size }) : t('users.bulkSuspendTitle', { count: selected.size })}
        message={bulkDialog.action === 'activate'
          ? t('users.bulkActivateBody', { count: selected.size })
          : t('users.bulkSuspendBody', { count: selected.size })}
        variant={bulkDialog.action === 'suspend' ? 'danger' : 'warning'}
        loading={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate({ ids: [...selected], isActive: bulkDialog.action === 'activate' })}
        onCancel={() => setBulkDialog({ open: false, action: null })}
      />
    </div>
  )
}

function UserRow({ user, lang, last, checked, onCheck, onActivate, onSuspend, onView, t }) {
  const roleMeta = ROLE_COLORS[user.role] || ROLE_COLORS.customer
  const roleName = ROLE_LABELS[user.role]?.[lang === 'ar' ? 'ar' : 'en'] || user.role
  const isActive = user.isActive

  return (
    <tr className="group" style={{ borderBottom: last ? 'none' : '1px solid #F3F3F3', transition:'background 0.15s', background: checked ? 'rgba(0,6,102,0.03)' : 'transparent' }}
      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#F9F9F9' }}
      onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent' }}
    >
      {/* Checkbox */}
      <td style={{ padding:'14px 16px', width:40 }}>
        <button onClick={onCheck} style={{ background:'none', border:'none', cursor:'pointer', color: checked ? '#000666' : '#C4C4C4', display:'flex', alignItems:'center' }}>
          {checked ? <CheckSquare size={16}/> : <Square size={16}/>}
        </button>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:36, height:36, borderRadius:10, background: isActive ? '#000666' : '#767683', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:700, flexShrink:0 }}>
            {user.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#1A1C1C' }}>{user.name}</div>
            <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:11, color:'#767683' }}>#{user.id?.slice(0,8)}</div>
          </div>
        </div>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#454652' }}>{user.email}</span>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#454652' }}>{user.phone || '—'}</span>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:8, background:roleMeta.bg, color:roleMeta.color, fontFamily:'Cairo, sans-serif' }}>{roleName}</span>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <span className={isActive ? 'badge-active' : 'badge-suspended'}>{isActive ? t('common.active') : t('common.suspended')}</span>
      </td>
      {/* Verified */}
      <td style={{ padding:'14px 16px' }}>
        <span style={{
          fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:8,
          background: user.isVerified ? 'rgba(27,109,36,0.08)' : 'rgba(186,26,26,0.08)',
          color: user.isVerified ? '#1B6D24' : '#BA1A1A',
          fontFamily:'Cairo, sans-serif',
        }}>
          {user.isVerified ? t('users.verifiedBadge') : t('users.unverifiedBadge')}
        </span>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <span style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683' }}>{formatDate(user.createdAt, lang === 'ar' ? 'ar-EG' : 'en')}</span>
      </td>
      <td style={{ padding:'14px 16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <button
            onClick={onView}
            style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:700, background:'rgba(0,6,102,0.07)', color:'#000666', fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', transition:'all 0.15s' }}
            onMouseEnter={(e) => e.currentTarget.style.background='rgba(0,6,102,0.14)'}
            onMouseLeave={(e) => e.currentTarget.style.background='rgba(0,6,102,0.07)'}
          >
            <ExternalLink size={12} />{t('common.view')}
          </button>
          {isActive ? (
            <button onClick={(e) => { e.stopPropagation(); onSuspend() }} style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:700, background:'rgba(186,26,26,0.08)', color:'#BA1A1A', fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', transition:'all 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background='rgba(186,26,26,0.15)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background='rgba(186,26,26,0.08)' }}
            >
              <UserX size={12} />{t('users.suspend')}
            </button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onActivate() }} style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:700, background:'rgba(27,109,36,0.08)', color:'#1B6D24', fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', transition:'all 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background='rgba(27,109,36,0.15)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background='rgba(27,109,36,0.08)' }}
            >
              <UserCheck size={12} />{t('users.activate')}
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}
